import { ROUTES } from "../constants";
import { getEffectiveIsDisplayInNewTab } from "./displayMode";
import { projectTabTitleMap } from "@lhd/features/ProjectManager/constants/project";

type BreadcrumbItem = {
  title?: string;
  url?: string;
};

const CHART_ROUTES: string[] = [
  ROUTES.CORRELATION_DIAGRAM,
  ROUTES.PERMISSION_SUMMARIES,
  ROUTES.CUSTOMER_HISTORY_SUMMARIES,
  ROUTES.BIZ_SHEET_SUMMARIES,
  ROUTES.PROJECT_CHART,
  ROUTES.ELECTRONIC_LEARNING_CHART,
  ROUTES.COMPANY_POLICIES_CHART,
  ROUTES.BUSINESS_CONTROLS_CHART,
  ROUTES.APPRAISAL_SUMMARIES,
  ROUTES.AGENT_SUMMARIES,
];

// Breadcrumb label for the ビジネスRPG play screen. The browser tab keeps the
// plain screen name in every mode, so this suffix is breadcrumb-only.
export const getBusinessRpgPlayLabel = (searchParams: URLSearchParams) => {
  if (!searchParams.get("recordId")) return "ビジネスRPG新規";

  // A freshly saved report lands on ?recordId=... without a pageMode, and the
  // author is still editing it, so edit is the fallback.
  return searchParams.get("pageMode") === "view"
    ? "ビジネスRPG閲覧"
    : "ビジネスRPG編集";
};

export const getTabTitle = ({
  pathname,
  searchParams,
  breadcrumbItems,
}: {
  pathname: string;
  searchParams: URLSearchParams;
  breadcrumbItems: string[];
}): string => {
  const isDisplayInNewTab = getEffectiveIsDisplayInNewTab(
    searchParams.get("isDisplayInNewTab") === "true",
  );

  const callBackUrl = searchParams.get("callBackUrl");

  const cleanPath = pathname.replace(/^\/+|\/+$/g, "");
  const patchName = cleanPath.split("/");

  // Check if route is a chart screen (Case 2)
  const isAnnualOrgChart = patchName[0] === ROUTES.ANNUAL_ORG_SUMMARIES;
  const isContractChart = patchName[0] === ROUTES.CONTRACT_MANAGEMENT;
  const isAnnualSalaryChart =
    patchName[0] === ROUTES.ANNUAL_SALARY_SUMMARIES && !patchName[1];
  const isStandardChart = CHART_ROUTES.includes(patchName[0]);

  const isChartPage =
    isStandardChart ||
    isAnnualOrgChart ||
    isContractChart ||
    isAnnualSalaryChart;

  const isSettingCategory =
    patchName[0] === ROUTES.SETTING_CATEGORIES &&
    searchParams.get("type") === "category";

  const pmtab = searchParams.get("pmtab");

  const pageSkipCheckIsDisplayInNewTab = [
    ROUTES.BUSINESS_RPG,
    ROUTES.BUSINESS_RPG_PLAY,
  ];

  const pageChildOfCompanyDetail = [
    ROUTES.FINANCIAL_REPORTS,
    ROUTES.EMPLOYMENT_CONTRACTS,
    ROUTES.AGREEMENTS,
    ROUTES.LABOR_DOCUMENTS,
  ];

  if (patchName[0] === ROUTES.LATEST_TRIAL_BALANCE_FIRE) {
    return "ＦＩＲＥ RPG新規作成";
  }

  if (patchName[0] === ROUTES.FIRE_RPG_MARKET_ECONOMY_VIEW) {
    return "市場チャート・経済ビュー";
  }

  if (
    !pageSkipCheckIsDisplayInNewTab.includes(patchName[0]) &&
    !isDisplayInNewTab
  ) {
    return "メニュー";
  }

  // The tab stays the plain screen name in every mode; the breadcrumb is what
  // spells out 新規 / 編集 / 閲覧 via getBusinessRpgPlayLabel.
  if (patchName[0] === ROUTES.BUSINESS_RPG && patchName[1] === "play") {
    return "ビジネスRPG";
  }

  if (patchName[0] === ROUTES.TRACKING_TIME_RAW) {
    if (patchName[1] === ROUTES.LIST.replace(/^\//, "")) {
      return "勤怠管理";
    }
    return "勤怠実績入力";
  }

  // Special overrides for screens with sub-tabs or detail/edit modes
  if (patchName[0] === ROUTES.PERMISSIONS_MANAGEMENT) {
    return "権限設定";
  }

  if (patchName[0] === ROUTES.ANNUAL_SALARY_SUMMARIES) {
    if (patchName?.[2] === ROUTES.SALARY_SHEET_FOR_WORKERS) {
      return "支給控除一覧";
    }
  }

  if (patchName[0] === ROUTES.LIST_MESS) {
    return "カスタマー問合せ";
  }

  if (patchName[0] === ROUTES.SETTING_CATEGORIES) {
    if (isSettingCategory) {
      return "カテゴリー";
    }
    return "ステータス";
  }

  if (patchName[0] === ROUTES.PROJECT_MANAGER) {
    if (!pmtab) {
      return "プロジェクト一覧";
    }
    // case task detail
    if (patchName?.length === 4) {
      return "タスク詳細";
    }
    return projectTabTitleMap[pmtab];
  }

  if (patchName[0] === ROUTES.WORKERS) {
    if (patchName?.[1] === ROUTES.CREATE) {
      return "スタッフ新規";
    } else if (/^\d+$/.test(patchName?.[1] ?? "")) {
      return "スタッフ詳細";
    }
    return "スタッフ一覧";
  }

  if (patchName[0] === ROUTES.AUTH) {
    return "スタッフ一覧";
  }

  if (patchName[0] === ROUTES.CUSTOMER_LIST) {
    if (patchName?.[1] === ROUTES.CREATE) {
      return "取引先情報";
    }
    return "取引先一覧";
  }

  if (patchName[0] === ROUTES.APPS) {
    if (patchName?.[1] === ROUTES.CREATE) {
      return "Bizシート新規";
    }
    if (/^\d+$/.test(patchName?.[1] ?? "")) {
      const element = document.querySelector(".breadcrumb-item-active p");
      if (element?.textContent) {
        return element.textContent;
      }
      return "Bizシート詳細";
    }
    const tab = searchParams.get("tab");
    if (tab === "list-app") return "Bizシート一覧";
    if (tab === "store-app") return "Bizシートストア";
    return "Bizシート";
  }

  if (patchName[0] === ROUTES.Electronic_Learning) {
    if (patchName[1] === ROUTES.STORE) {
      return "Eラーニングストア";
    }
    if (patchName[2] === ROUTES.CREATE) {
      return "コース新規";
    }
    return "Eラーニング一覧";
  }

  if (patchName[0] === ROUTES.PERFORMANCE_APPRAISAL) {
    const patchNameDetail = patchName[1];
    switch (patchNameDetail) {
      case "appraisals":
        return "目標一覧";
      case "appraisal_sheets":
        return "評価シート一覧";
      case "config":
        return "人事評価設定";
      case "appraisal_analyst":
        return "評価結果分析";
      case "appraisal_kgi_list":
        return "行動目標一覧";
      default:
        return "人事評価";
    }
  }
  if (pageChildOfCompanyDetail.includes(patchName[0])) {
    if (callBackUrl?.includes(ROUTES.COMPANY_CONFIGURATION)) {
      return "企業情報";
    }
    return "契約会社";
  }

  // Basic case (Rule 1): Get the last item in breadcrumb with a title (ignoring default "階層パス")
  const validItems = (breadcrumbItems || []).filter(
    (item) => item && item !== "階層パス",
  );

  if (validItems.length > 0) {
    const lastItemTitle = validItems[validItems.length - 1];
    if (lastItemTitle) {
      if (
        patchName[0] === ROUTES.COMPANY_POLICIES &&
        patchName[2] === ROUTES.CREATE
      ) {
        return lastItemTitle.replace(/^[＋+]\s*/, "");
      }
      return lastItemTitle;
    }
  }

  return "BizTreasure";
};
